import configs from 'configs';
import { getCaptchaResponse, removeCaptchaResponse, saveItem } from 'utils';
import { callRequest } from 'services/callRequestManager';
import { Authentification, HttpRequest, RequestCode, CreateProfileResponse } from 'types/interfaces';
import { getBaoHeaders } from 'utils/productFormUtils';
import { UAParser } from 'ua-parser-js';
import { fromState } from 'globalRedux/store';
import { RECAPTCHA_KEY, RECAPTCHA_SITE_KEY_TYPE } from 'utils/constants';
import * as RootConfigs from 'configs';

export const saveAlternateContentError = (errorCode: string) => saveItem('bot-manager-reference', errorCode);

export const AUTHENT_TYPE = 'authentType';
export const BAO_AUTHENT_TYPE = 'BAO';
export const OKTA_AUTHENT_TYPE = 'OKTA';
export type ALL_AUTHENT_TYPES = typeof BAO_AUTHENT_TYPE | typeof OKTA_AUTHENT_TYPE;

/**
 * Check wether the user is authenticated with the given method.
 * @param authentType One of the authentication method.
 */
export const isAuthenticatedWith = (authentType: ALL_AUTHENT_TYPES): boolean => {
  return localStorage.getItem(AUTHENT_TYPE) === authentType;
};

/**
 * Store the current authentication method.
 * @param authentType One of the authentication method.
 */
export const setAuthenticatedWith = (authentType: ALL_AUTHENT_TYPES): void => {
  localStorage.setItem(AUTHENT_TYPE, authentType);
};

/**
 * Remove the current authentication method from memory.
 */
export const clearAuthenticationWith = (): void => {
  localStorage.removeItem(AUTHENT_TYPE);
};

export const requestConfig = (endpoint: string, headers?: HeadersInit): HttpRequest => {
  const url = `${configs.params.LOGIN_MANAGER_SERVER.BASE_URL}${configs.params.LOGIN_MANAGER_SERVER.ENDPOINTS[endpoint]}`;
  const method = 'POST';
  return {
    url,
    method,
    headers,
  };
};

export const baoLoginRequest = 'baoLoginRequest';
export const oktaLoginRequest = 'oktaLoginRequest';

export const authenticateWithBAO = async (
  endpoint: string,
  body: { requestPassword: string; requestCode?: string; '@type': string },
): Promise<void> => {
  if (body.requestPassword === '' || body.requestCode === '') return Promise.resolve();

  const headers: Record<string, string> = {};
  const captchaResponse = getCaptchaResponse();
  if (captchaResponse) {
    headers[RECAPTCHA_KEY] = captchaResponse;
    headers[RECAPTCHA_SITE_KEY_TYPE] = RootConfigs.getConfig().CAPTCHA.SITE_KEY_FRONTEND;
    removeCaptchaResponse();
  }

  const config: HttpRequest = {
    ...requestConfig(endpoint, headers),
    body: JSON.stringify(body),
  };

  await callRequest<Authentification>(config);
  setAuthenticatedWith(BAO_AUTHENT_TYPE);
};

export const signup = (requestPassword: string = '') =>
  authenticateWithBAO('SIGN_UP', { requestPassword, '@type': baoLoginRequest });

export const signin = (requestPassword: string = '', requestCode: string = '') =>
  authenticateWithBAO('SIGN_IN', { requestPassword, requestCode, '@type': baoLoginRequest });

export const logout = async (): Promise<void> => {
  const config: HttpRequest = requestConfig('LOG_OUT', getBaoHeaders());
  await callRequest<void>(config);
  return Promise.resolve();
};

export const signinSSO = (): Promise<void> =>
  callRequest<Authentification>({
    url: `${configs.params.LOGIN_MANAGER_SERVER.BASE_URL}${configs.params.LOGIN_MANAGER_SERVER.ENDPOINTS.SIGN_IN}`,
    method: 'POST',
    body: JSON.stringify({ '@type': oktaLoginRequest }),
  }).then(() => setAuthenticatedWith(OKTA_AUTHENT_TYPE));

export const signupSSO = (): Promise<void> =>
  callRequest<Authentification>({
    url: `${configs.params.LOGIN_MANAGER_SERVER.BASE_URL}${configs.params.LOGIN_MANAGER_SERVER.ENDPOINTS.SIGN_UP}`,
    method: 'POST',
    body: JSON.stringify({ '@type': oktaLoginRequest }),
  }).then(() => setAuthenticatedWith(OKTA_AUTHENT_TYPE));

export const createUserProfile = (
  title: string = '',
  firstName: string = '',
  middleName: string = '',
  lastName: string = '',
  birthDate: string = '',
  language: string = '',
): Promise<CreateProfileResponse | null> => {
  if (title === '' || firstName === '' || lastName === '' || birthDate === '' || language === '') {
    return Promise.resolve(null);
  }

  const headers: Record<string, string> = {};

  const captchaResponse = getCaptchaResponse();

  if (captchaResponse) {
    headers[RECAPTCHA_KEY] = captchaResponse;
    headers[RECAPTCHA_SITE_KEY_TYPE] = RootConfigs.getConfig().CAPTCHA.SITE_KEY_FRONTEND;
    removeCaptchaResponse();
  }

  return callRequest<CreateProfileResponse>({
    url: `${configs.params.LOGIN_MANAGER_SERVER.BASE_URL}${configs.params.LOGIN_MANAGER_SERVER.ENDPOINTS.CREATE_PROFILE}`,
    method: 'POST',
    headers: headers,
    body: JSON.stringify({
      title,
      firstName,
      middleName,
      lastName,
      birthDate,
      language,
      email: fromState<string, null>('baoReducer.userCredentials.email', ''),
      password: fromState<string, null>('baoReducer.userCredentials.encryptedPassword', ''),
    }),
  });
};

export const validateApplication = (): Promise<RequestCode> => {
  return callRequest<RequestCode>({
    url: `${configs.params.LOGIN_MANAGER_SERVER.BASE_URL}${configs.params.LOGIN_MANAGER_SERVER.ENDPOINTS.PROFILE_ACCOUNT_REQUEST}`,
    method: 'GET',
    headers: getBaoHeaders(),
  });
};

export const getUserDeviceType = (parser: UAParser): string => {
  const device = parser.getDevice(); // Possible 'device.type': console, mobile, tablet, smarttv, wearable, embedded

  return device && device.type && device.type === 'mobile' ? 'deviceType=MOBILE' : 'deviceType=DESKTOP';
};
